// ============================================================
// ForcedTwoFactorSetup.tsx
// Mandatory 2FA enrolment gate. Rendered by App.tsx at the auth seam —
// AFTER the user is authenticated but BEFORE the normal app chrome —
// when their FIRM requires 2FA (authUser.firmRequire2fa) and they don't
// yet have it (!authUser.twoFactorEnabled).
//
// It is standalone by design: no sidebar, no app header, and crucially
// NO skip / dismiss / close. The ONLY way out is to complete real
// enrolment. It REUSES the existing TwoFactorPanel enrolment flow
// (setup → QR + code → confirm → one-time backup codes) rather than
// rebuilding it — the panel already owns setup2faApi/confirm2faApi and
// the backup-codes step.
//
// COMPLETION → FALL-THROUGH: TwoFactorPanel fires onComplete only once
// the user confirms a code AND acknowledges their backup codes ("Done").
// We then refresh the cached user via getMe; the fresh profile has
// twoFactorEnabled:true, so App.tsx's gate condition
// (firmRequire2fa && !twoFactorEnabled) turns false and the app renders
// normally — this whole screen unmounts.
//
// MID-ENROLMENT: a user who started but never confirmed setup has a
// UserTwoFactor row with enabled:false, so getMe reports
// twoFactorEnabled:false and they still see this gate (correct — they
// must finish). setup2faApi is an upsert that only refuses when 2FA is
// already ENABLED, so re-starting from a pending row simply regenerates
// the secret without erroring.
// ============================================================
import React from 'react'
import TwoFactorPanel from '../shared/TwoFactorPanel'
import { useCasevoStore } from '../../store/useCasevoStore'
import { getMeApi } from '../../api/casevoApi'

const ForcedTwoFactorSetup: React.FC = () => {
  const setAuthUser = useCasevoStore((s) => s.setAuthUser)

  // Enrolment done + codes saved → re-hydrate the user so twoFactorEnabled
  // flips true and App.tsx's gate falls through. Failure is logged, not
  // fatal: the panel already shows "2FA is on"; a later getMe (App's
  // mount refresh) will still flip the flag, so the user is never stuck.
  const handleComplete = (): void => {
    getMeApi()
      .then((user) => setAuthUser(user))
      .catch((err) => console.error('[ForcedTwoFactorSetup] getMe refresh failed', err))
  }

  return (
    <div className="min-h-screen bg-casevo-offwhite font-sans flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">
            Security required
          </p>
          <h1 className="text-2xl font-medium text-casevo-forest mb-2">
            Your firm requires two-factor authentication
          </h1>
          <p className="text-sm text-casevo-mid">
            Set up 2FA to continue. This is a one-time step — once it&rsquo;s
            active you&rsquo;ll get straight into the app.
          </p>
        </div>
        {/* Reused enrolment flow. `standalone` drops the panel's settings
            card chrome + own heading and centres its inner elements so it
            sits cleanly under the heading above. onComplete → getMe
            refresh → gate falls through. No skip/close anywhere. */}
        <TwoFactorPanel standalone onComplete={handleComplete} />
        {/* Logout escape hatch. This is NOT a skip — completing 2FA is the
            only way INTO the app. Logging out grants no access; it just
            clears the session and returns to the login screen (App.tsx
            re-renders the unauthenticated branch when clearAuth flips
            isAuthenticated false), so a user can leave or switch accounts
            without being trapped. Deliberately subtle + secondary to the
            setup above; reuses the same clearAuth action as Settings /
            Sidebar sign-out. */}
        <div className="mt-6 text-center">
          <p className="text-xs text-casevo-mid">
            Need to use a different account?{' '}
            <button
              type="button"
              onClick={() => useCasevoStore.getState().clearAuth()}
              className="underline text-casevo-mid hover:text-casevo-forest transition-colors"
            >
              Log out
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default ForcedTwoFactorSetup
